import { ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { config } from "../config/app";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Gets the CloudFront URL with optional path
 * @param path Optional path to append to the CloudFront URL
 * @returns Full CloudFront URL
 */
export function getCloudFrontUrl(path?: string): string {
  const baseUrl = `https://${config.urls.cloudfront || "da5tspnozu0py.cloudfront.net"}`;
  return path ? `${baseUrl}/${path.replace(/^\//, "")}` : baseUrl;
}

/**
 * Gets the website URL with optional path
 * @param path Optional path to append to the website URL
 * @returns Full website URL
 */
export function getWebsiteUrl(path?: string): string {
  const websiteUrl = config.urls.website || "https://cv-automation-demo.dev.scvl.jp";
  const baseUrl = websiteUrl.replace(/\/$/, "");
  return path ? `${baseUrl}/${path.replace(/^\//, "")}` : baseUrl;
}

/**
 * Gets the hosted zone name
 * @returns The hosted zone name
 */
export function getHostedZoneName(): string {
  return config.urls.hostedZone || "cv-automation-demo.dev.scvl.jp";
}

/**
 * Format timestamp to a user-friendly local date and time
 * Handles various timestamp formats including ISO 8601 with timezone info
 *
 * @param timestamp The timestamp string to format
 * @param options Optional formatting options
 * @returns A user-friendly formatted date string
 */
export function formatTimestamp(
  timestamp: string,
  options: {
    roundToMinute?: boolean;
    format?: Intl.DateTimeFormatOptions;
  } = {}
): string {
  try {
    if (!timestamp) return "No date";

    // Default options
    const defaultOptions = {
      roundToMinute: true,
      format: {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      } as Intl.DateTimeFormatOptions,
    };

    // Merge with user options
    const mergedOptions = { ...defaultOptions, ...options };

    // Handle specific format issues first
    // Remove any trailing 'Z' after timezone offset (which causes parsing issues)
    let cleanTimestamp = timestamp;
    // Match pattern like "+09:00Z" and remove the 'Z'
    if (/[+-]\d{2}:\d{2}Z$/.test(cleanTimestamp)) {
      cleanTimestamp = cleanTimestamp.replace(/([+-]\d{2}:\d{2})Z$/, "$1");
    }

    // Create a date object from the cleaned timestamp
    const date = new Date(cleanTimestamp);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error("Invalid date from timestamp:", timestamp, "cleaned to:", cleanTimestamp);

      // Try using manual parsing as fallback
      const matches = timestamp.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
      if (matches) {
        const [, year, month, day, hour, minute, second] = matches;
        const manualDate = new Date(
          parseInt(year),
          parseInt(month) - 1, // Month is 0-indexed
          parseInt(day),
          parseInt(hour),
          parseInt(minute),
          parseInt(second)
        );

        if (!isNaN(manualDate.getTime())) {
          return formatDateOutput(manualDate, mergedOptions);
        }
      }

      return "Invalid date";
    }

    return formatDateOutput(date, mergedOptions);
  } catch (error) {
    console.error("Error formatting date:", error, "for timestamp:", timestamp);
    return "Date error";
  }
}

/**
 * Helper function to format a Date object with consistent output
 *
 * @param date The Date object to format
 * @param options Formatting options
 * @returns Formatted date string
 */
function formatDateOutput(
  date: Date,
  options: {
    roundToMinute?: boolean;
    format?: Intl.DateTimeFormatOptions;
  }
): string {
  // Apply rounding if requested
  let dateToFormat = date;
  if (options.roundToMinute) {
    dateToFormat = new Date(Math.ceil(date.getTime() / (1000 * 60)) * 1000 * 60);
  }

  return dateToFormat.toLocaleString(undefined, options.format);
}
